import React from 'react'

import {flatten} from 'es-toolkit'
import {isNumber, orderBy, some} from 'es-toolkit/compat'
import invariant from 'invariant'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {openPartialRefundDetailsModal, openSimpleContentModal} from '../../redux/features/modal'

import {isBeforeDay, isTodaysDate} from '../../../../commons/src/services/date'
import {downloadBlob} from '../../../../commons/src/services/download'
import {formatDate, formatDateTime, formatDeliveryTime, formatPrice} from '../../libs/formatter'
import {getProductionTime, sanitizeHistory, statusIsClosedOrShipped} from '../../libs/order'
import * as internalApi from '../../services/api/internal'
import * as partnerApi from '../../services/api/partner'
import {captureException} from '../../services/logging'

import downloadIcon from '../../../../commons/src/assets/icons/download-box.svg'
import fileNoteIcon from '../../../../commons/src/assets/icons/file-note.svg'
import infoIcon from '../../../../commons/src/assets/icons/info.svg'
import uploadIcon from '../../../../commons/src/assets/icons/upload.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Badge from '../../../../commons/src/components/badge/Badge'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Newlines from '../../../../commons/src/components/newlines'
import Paragraph from '../../../../commons/src/components/paragraph'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import TruncatedLabel from '../../../../commons/src/components/truncated-label'
import Container from '../../components/container'
import OrderStatusBadge from '../../components/order-status-badge'
import OrderTag from '../../components/order-tag'

import {
  BackendModelAttachment,
  ModelId,
  Order,
  OrderId,
  RefundStatus,
  RefundType,
} from '../../order-types'

import AdditionalCostPartial from './additional-cost-partial'
import ModelTablePartial from './model-table-partial'
import OrderTrackingPartial from './order-tracking-partial'
import ProductionDetailsModal from './production-details-modal-content'

type Props = {
  order: Order
  onOrderUpdate: (updatedOrder: Order) => void
}

const OrderDetailsPartial = ({order, onOrderUpdate}: Props) => {
  const dispatch = useAppDispatch()

  const countries = useAppSelector(state => state.core.countries)
  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)
  const provider = useAppSelector(state => state.core.provider)
  const api = isAdminMode ? internalApi : partnerApi

  invariant(provider, 'Provider is missing.')

  const [isDownloadingAll, setIsDownloadingAll] = React.useState(false)
  const [downloadAllError, setDownloadAllError] = React.useState<string>()

  const currency =
    order.amounts.cartItems?.currency || order.amounts.additionalCosts?.currency || ''
  const shippingCurrency = order.amounts.shipping?.currency || ''

  const orderHistory = React.useMemo(() => sanitizeHistory(order), [order])

  const handleModelDownloadAll = () => {
    setIsDownloadingAll(true)
    setDownloadAllError(undefined)

    api
      .getModelDownloadAll(order.orderId, provider.id)
      .then(result => {
        downloadBlob(result, `models_order#${order.orderNumber}`).finally(() =>
          setIsDownloadingAll(false)
        )
      })
      .catch(error => {
        captureException(error)
        setDownloadAllError('Download all failed, please try again.')
      })
      .finally(() => setIsDownloadingAll(false))
  }

  const renderCustomerInformation = () => {
    const customer = order.customer
    const stateName = order.customer.stateCode

    return (
      <Stack space="80">
        <Headline label="Customer" className="u-no-margin-bottom" />
        <Paragraph size="s">
          {`${customer.firstName} ${order.customer.lastName}`}
          <br />
          {customer.companyName && (
            <>
              {customer.companyName}
              <br />
            </>
          )}
          {customer.address}
          <br />
          {customer.addressLine2 && (
            <>
              {customer.addressLine2}
              <br />
            </>
          )}
          {order.customer.city}
          {stateName && <>, {stateName}</>} {customer.zipCode}
          <br /> {countries[customer.countryCode].name}
          <br />
          Phone: {customer.phoneNumber}
          {customer.vatId && (
            <>
              <br />
              VAT ID: {customer.vatId} <br />
            </>
          )}
        </Paragraph>
      </Stack>
    )
  }

  const renderCustomsInformation = () => {
    const description = order.customsInformation?.description || '- N/A -'
    const industry = order.customsInformation?.industry
      ? config.industryCategoryMap[
          order.customsInformation.industry as keyof typeof config.industryCategoryMap
        ]
      : '- N/A -'

    return (
      <Stack space="70">
        <ActionBar space="80">
          <Paragraph size="s" strong className="u-no-margin-bottom">
            Customs Information
          </Paragraph>
          <LinkButton
            onClick={() => {
              dispatch(
                openSimpleContentModal({
                  title: 'Customs information',
                  content: (
                    <Stack>
                      <Stack space="80">
                        <Headline size="s" label="Industry" className="u-no-margin-bottom" />
                        <Paragraph className="u-no-margin-bottom">{industry}</Paragraph>
                      </Stack>
                      <Stack space="80">
                        <Headline size="s" label="Description" className="u-no-margin-bottom" />
                        <Paragraph className="u-no-margin-bottom">
                          <Newlines text={description} />
                        </Paragraph>
                      </Stack>
                    </Stack>
                  ),
                })
              )
            }}
          >
            show
          </LinkButton>
        </ActionBar>
        <TruncatedLabel size="xs" maxWidth>
          {`Industry: ${industry}`}
          <br />
          {`Description: "${description}"`}
        </TruncatedLabel>
      </Stack>
    )
  }

  const renderShippingInformation = () => {
    const isAvailable = order.shipping.vendorId
    if (!isAvailable) {
      return (
        <Stack space="80">
          <Headline label="Shipping" className="u-no-margin-bottom" />
          <Paragraph size="xs" minor className="u-no-margin-bottom">
            <i>no shipping information available</i>
          </Paragraph>
          <OrderTrackingPartial order={order} onOrderUpdate={onOrderUpdate} />
        </Stack>
      )
    }
    const currentStatus = order.printingService.status

    return (
      <Stack space="80">
        <Headline label="Shipping" className="u-no-margin-bottom" />
        <Stack>
          <Stack space="60">
            <ActionBar space="70" noWrap>
              <Paragraph size="s" className="u-no-margin-bottom">
                {order.shipping.name}
              </Paragraph>
              {order.shipping.type === 'express' && <OrderTag type="express" />}
            </ActionBar>
            {order.shipping.deliveryTime && (
              <Paragraph size="s" className="u-no-margin-bottom">
                {formatDeliveryTime(order.shipping.deliveryTime)}
              </Paragraph>
            )}
            <ActionBar space="80">
              <Paragraph size="s" className="u-no-margin-bottom">
                Customer price:
              </Paragraph>
              <Paragraph size="s" strong className="u-no-margin-bottom">
                {formatPrice(order.amounts.shipping.netPrice, currency, 2)}
              </Paragraph>
            </ActionBar>
            {isNumber(order.amounts.invoiceable?.shipping) && (
              <>
                <ActionBar space="80">
                  <Paragraph size="s" minor className="u-no-margin-bottom">
                    Your price:
                  </Paragraph>
                  <Paragraph size="s" minor className="u-no-margin-bottom">
                    {formatPrice(order.amounts.shipping?.originalPrice, shippingCurrency, 2)}
                  </Paragraph>
                </ActionBar>
                <ActionBar space="80">
                  <Paragraph size="s" minor className="u-no-margin-bottom">
                    Your earnings:
                  </Paragraph>
                  <Paragraph size="s" strong minor className="u-no-margin-bottom">
                    {formatPrice(order.amounts.invoiceable.shipping, shippingCurrency, 2)}
                  </Paragraph>
                </ActionBar>
              </>
            )}
          </Stack>
          <Stack space="60">
            <ActionBar>
              <Paragraph size="s" className="u-no-margin-bottom">
                Ship by:
              </Paragraph>
              <Paragraph
                size="s"
                strong
                className="u-no-margin-bottom"
                warning={
                  !statusIsClosedOrShipped(currentStatus) && isTodaysDate(order.shippingDueDate)
                }
                errorIcon={
                  !statusIsClosedOrShipped(currentStatus) &&
                  isBeforeDay(order.shippingDueDate, new Date())
                }
              >
                {formatDate(order.shippingDueDate)}
              </Paragraph>
            </ActionBar>
            {order.shipping.carrier && (
              <ActionBar>
                <Paragraph size="s" className="u-no-margin-bottom">
                  Carrier:
                </Paragraph>
                <Paragraph size="s" strong className="u-no-margin-bottom">
                  {order.shipping.carrier}
                </Paragraph>
              </ActionBar>
            )}
            <OrderTrackingPartial order={order} onOrderUpdate={onOrderUpdate} />
          </Stack>
          {order.customsInformation && renderCustomsInformation()}
        </Stack>
      </Stack>
    )
  }

  const renderSubtotalInformation = () => {
    const isAvailable = order.quotes.length > 0 && order.amounts.cartItems.netPrice
    if (!isAvailable) {
      return null
    }
    return (
      <Stack space="80">
        <Headline label="Production" className="u-no-margin-bottom" />
        <Stack space="60">
          <Paragraph size="s" className="u-no-margin-bottom">
            {formatDeliveryTime(getProductionTime(order.quotes))}
          </Paragraph>
          <ActionBar space="80">
            <Paragraph size="s" className="u-no-margin-bottom">
              Customer price:
            </Paragraph>
            <Paragraph size="s" strong className="u-no-margin-bottom">
              {formatPrice(order.amounts.cartItems.netPrice, currency, 2)}
            </Paragraph>
          </ActionBar>
          {order.amounts.invoiceable?.production && (
            <>
              <ActionBar space="80">
                <Paragraph size="s" minor className="u-no-margin-bottom">
                  Your price:
                </Paragraph>
                <Paragraph size="s" minor className="u-no-margin-bottom">
                  {formatPrice(order.amounts.cartItems?.originalPrice, currency, 2)}
                </Paragraph>
              </ActionBar>
              <ActionBar space="80">
                <Paragraph size="s" minor className="u-no-margin-bottom">
                  Your earnings:
                </Paragraph>
                <Paragraph size="s" strong minor className="u-no-margin-bottom">
                  {formatPrice(order.amounts.invoiceable?.production, currency, 2)}
                </Paragraph>
              </ActionBar>
            </>
          )}
        </Stack>
      </Stack>
    )
  }

  const renderStatusHistory = () => (
    <Stack space="80">
      <Headline label="History" className="u-no-margin-bottom" />
      <SplitLayout
        left={<Badge size="s" label="Created" type="base" icon={<Icon source={uploadIcon} />} />}
        right={
          <Paragraph size="s" minor className="u-no-margin-bottom u-margin-left-s">
            {formatDateTime(order.created)}
          </Paragraph>
        }
      />
      {orderHistory.map(historyItem => (
        <SplitLayout
          key={historyItem.date}
          left={<OrderStatusBadge type={historyItem.status} />}
          right={
            <Paragraph size="s" minor className="u-no-margin-bottom u-margin-left-s">
              {formatDateTime(historyItem.date)}
            </Paragraph>
          }
        />
      ))}
    </Stack>
  )

  const renderOrderNote = (orderNote: string) => (
    <TruncatedLabel
      size="s"
      minor
      maxWidth
      postfix={
        <LinkButton
          onClick={() => {
            dispatch(
              openSimpleContentModal({title: 'Note', content: <Paragraph>{orderNote}</Paragraph>})
            )
          }}
        >
          show
        </LinkButton>
      }
    >
      <Icon source={fileNoteIcon} className="u-margin-right-xs" />
      {`"${orderNote}"`}
    </TruncatedLabel>
  )

  const renderProductionDetails = (
    orderId: OrderId,
    modelId: ModelId,
    note = '',
    attachments: BackendModelAttachment[] = []
  ) => {
    const showProductionDetailsModal = () => {
      dispatch(
        openSimpleContentModal({
          title: 'Production details',
          content: (
            <ProductionDetailsModal
              note={note}
              attachments={attachments}
              orderId={orderId}
              modelId={modelId}
            />
          ),
        })
      )
    }

    const attachmentCount = attachments?.length ?? 0
    const hasProductionDetails = note || !!attachmentCount

    return (
      hasProductionDetails && (
        <Stack space="60">
          <Paragraph strong className="u-no-margin-bottom">
            Production details
          </Paragraph>
          <TruncatedLabel
            title={note}
            postfix={
              <>
                {note && <>&quot;</>}
                {attachmentCount
                  ? note
                    ? ` (${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''})`
                    : `${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`
                  : null}{' '}
                <LinkButton onClick={showProductionDetailsModal}>show</LinkButton>
              </>
            }
          >
            {note && <>&quot;{note}</>}
          </TruncatedLabel>
        </Stack>
      )
    )
  }

  const renderAdditionalCosts = () => (
    <Stack>
      <Stack space="60">
        <Headline
          label={`Additional Cost${(order.additionalCosts?.length || 0) > 1 ? 's' : ''}`}
          className="u-no-margin-bottom"
        />
        {!!order.amounts.invoiceable?.additionalCosts && (
          <ActionBar space="80">
            <Paragraph size="s" minor className="u-no-margin">
              Your earnings:
            </Paragraph>
            <Paragraph size="s" strong minor className="u-no-margin">
              {formatPrice(
                order.amounts.invoiceable?.additionalCosts,
                order.amounts.additionalCosts?.currency || '',
                2
              )}
            </Paragraph>
          </ActionBar>
        )}
      </Stack>
      {orderBy(order.additionalCosts, item => item.created, 'desc').map(additionalCost => (
        <AdditionalCostPartial key={additionalCost.created} additionalCost={additionalCost} />
      ))}
    </Stack>
  )

  const renderRefundRequests = () => (
    <Stack>
      <Headline
        label={`Refund Request${(order.refundRequests?.length || 0) > 1 ? 's' : ''}`}
        className="u-no-margin-bottom"
      />
      {orderBy(order.refundRequests, item => item.created, 'desc').map(refundRequest => (
        <Stack space="60" key={refundRequest.created}>
          <ActionBar alignItems="top">
            <Paragraph size="s" className="u-no-margin">
              {formatDateTime(refundRequest.created)}
            </Paragraph>
            <ActionBar>
              <Paragraph size="s" className="u-no-margin">
                <strong>{refundRequest.refundType}</strong> refund
              </Paragraph>
              <ActionBar space="70">
                <Paragraph size="s" className="u-no-margin">
                  &quot;
                  {flatten(Object.values(config.cancellationReasonOptionsMap)).find(
                    reason => reason.value === refundRequest.reason
                  )?.label || refundRequest.reason}
                  &quot;
                </Paragraph>
                {refundRequest.refundType === RefundType.CUSTOM && (
                  <LinkButton
                    icon={<Icon source={fileNoteIcon} />}
                    onClick={() => {
                      dispatch(
                        openSimpleContentModal({
                          title: 'Description',
                          content: <Paragraph>{refundRequest.customAmount?.description}</Paragraph>,
                        })
                      )
                    }}
                  />
                )}
                {refundRequest.refundType === RefundType.PARTIAL && (
                  <LinkButton
                    icon={<Icon source={infoIcon} />}
                    onClick={() => {
                      dispatch(openPartialRefundDetailsModal({order, refund: refundRequest}))
                    }}
                  />
                )}
              </ActionBar>
              <Paragraph size="s" className="u-no-margin">
                <strong>{formatPrice(refundRequest.totalNetAmount, currency, 2)}</strong>
              </Paragraph>
              <Paragraph
                size="s"
                strong
                className="u-no-margin"
                success={refundRequest.status === RefundStatus.APPROVED}
                warning={refundRequest.status === RefundStatus.PENDING}
                errorIcon={refundRequest.status === RefundStatus.REJECTED}
              >
                {refundRequest.status}
              </Paragraph>
            </ActionBar>
          </ActionBar>
        </Stack>
      ))}
    </Stack>
  )

  return (
    <Container>
      <Stack space="400">
        <SplitLayout
          left={
            <ActionBar space="500" alignItems="top" noWrap>
              {renderCustomerInformation()}
              {renderSubtotalInformation()}
              {renderShippingInformation()}
            </ActionBar>
          }
          right={renderStatusHistory()}
        />
        {config.refund.enabled &&
          order.refundRequests &&
          order.refundRequests.length > 0 &&
          renderRefundRequests()}
        {order.additionalCosts && order.additionalCosts.length > 0 && renderAdditionalCosts()}
        <Stack>
          <ActionBar space="80">
            <Headline
              label="Models"
              addendum={`(${order.quotes.length})`}
              className="u-no-margin-bottom"
            />
            {!isDownloadingAll && (
              <ActionBar space="80">
                <LinkButton
                  icon={<Icon source={downloadIcon} />}
                  iconAlign="right"
                  onClick={handleModelDownloadAll}
                  disabled={isDownloadingAll || order.quotes.length === 0}
                >
                  Download all models{' '}
                  {some(order.models, model => !!model.attachments && model.attachments.length > 0)
                    ? '(incl. attachments)'
                    : ''}
                </LinkButton>
                {downloadAllError && (
                  <Paragraph error size="s" className="u-no-margin-bottom">
                    {downloadAllError}
                  </Paragraph>
                )}
              </ActionBar>
            )}
            {isDownloadingAll && (
              <>
                <Paragraph size="s" minor className="u-no-margin-bottom">
                  Downloading…
                </Paragraph>
                <LoadingIndicator />
              </>
            )}
          </ActionBar>
          {order.note && renderOrderNote(order.note)}
          <ModelTablePartial
            order={order}
            quotes={order.quotes}
            renderProductionDetails={renderProductionDetails}
          />
        </Stack>
      </Stack>
    </Container>
  )
}

export default OrderDetailsPartial
